import { useTranslation } from 'react-i18next';

interface StepProgressProps {
  current: number;
  total: number;
  /** When provided, indicators become clickable buttons (F094 direct nav). */
  onStepClick?: (index: number) => void;
  /** Highest step index the owner is allowed to jump to. Steps beyond this are
   * disabled (first-config can't skip ahead past validation). Defaults to
   * `total - 1` (all reachable, e.g. editing a completed KB). */
  maxReached?: number;
}

export function StepProgress({
  current,
  total,
  onStepClick,
  maxReached = total - 1,
}: StepProgressProps) {
  const { t } = useTranslation();
  const interactive = typeof onStepClick === 'function';

  function barClass(i: number): string {
    return [
      'h-1.5 rounded-full transition-all duration-200',
      i === current ? 'w-6 bg-foreground' : i < current ? 'w-3 bg-foreground/50' : 'w-3 bg-border',
    ].join(' ');
  }

  return (
    <div
      className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3 mb-4"
      aria-label={t('onboarding.kbForm.progress', { current: current + 1, total })}
    >
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) =>
          interactive ? (
            <button
              key={i}
              type="button"
              onClick={() => onStepClick?.(i)}
              disabled={i > maxReached}
              aria-label={t('onboarding.kbForm.go_to_step', { step: i + 1 })}
              aria-current={i === current ? 'step' : undefined}
              className="flex min-h-11 min-w-8 sm:min-w-11 items-center justify-center disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className={barClass(i)} aria-hidden="true" />
            </button>
          ) : (
            <div key={i} className={barClass(i)} aria-hidden="true" />
          )
        )}
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {t('onboarding.kbForm.progress', { current: current + 1, total })}
      </span>
    </div>
  );
}
